<template>
	<div>
		<!-- dialog -->
		<v-dialog v-model="addEditDialog" max-width="550">
			<v-form ref="form" @submit.prevent="submit" :disabled="submitLoading">
				<v-card>
					<v-card-title>
						<span class="me-n6">
							{{
								(selectedItem.id ? $T('edit') : $T('add')) +
								' ' +
								(+tab === penaltyRewardType.penalty ? $T('penalty') : $T('reward'))
							}}
						</span>
						<v-spacer />
						<v-btn icon @click="addEditDialog = false">
							<v-icon>$close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-row dense class="ma-0">
							<!-- employeeId -->
							<v-col sm="6" cols="12">
								<v-autocomplete
									v-model="dialogData.employeeId"
									:label="$T('inputs.employee-name')"
									:items="employees"
									:item-text="item => `(${item.code}) ${item.arabicFullName}`"
									item-value="employeeId"
									hide-details
									dense
									outlined
									ref="employeeInput"
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>
							
							<!-- suggested-by -->
							<v-col sm="6" cols="12">
								<v-autocomplete
									v-model="dialogData.suggestedByEmployeeId"
									:label="$T('inputs.suggested-by')"
									:items="employees.filter(c => c.employeeId !== dialogData.employeeId)"
									:item-text="item => `(${item.code}) ${item.arabicFullName}`"
									item-value="employeeId"
									hide-details
									dense
									outlined
									ref="employeeInput"
								></v-autocomplete>
							</v-col>

							<!-- type -->
							<v-col sm="4" cols="12">
								<v-autocomplete
									v-model="dialogData.typeId"
									:label="
										+tab === penaltyRewardType.penalty
											? $T('inputs.penalty-type')
											: $T('inputs.reward-type')
									"
									:items="+tab === penaltyRewardType.penalty ? penalties : rewards"
									item-text="name"
									item-value="id"
									hide-details
									dense
									outlined
									@change="e => {
										const item = getTypeDueCurrentTab(e);
										dialogData.effectType = item.valueType;
										dialogData.typeValue = item.value;
									}"
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>

							<!-- effect-type -->
							<v-col sm="4" cols="12">
								<v-autocomplete
									v-model="dialogData.effectType"
									:items="tab === 0 ? penaltyTypes : rewordsTypes"
									item-value="id"
									item-text="name"
									:label="$T('inputs.effect-type')"
									hide-details
									dense
									outlined
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>

							<!-- value -->
							<v-col sm="4" cols="12">
								<v-text-field
									v-model.number="dialogData.typeValue"
									:label="typeValueLabel"
									hide-details
									:prefix="dialogData.effectType === penaltyRewardValueType.rate ? '%' : null"
									dense
									outlined
									:rules="[rules.isNumber]"
								></v-text-field>
							</v-col>
				
							<!-- date -->
							<v-col sm="6" cols="12">
								<bee-date-input
									v-model="dialogData.date"
									:label="$T('inputs.date')"
									hide-details
									dense
									prepend-icon="$calendar"
									user-menu-picker
									:rules="[rules.required]"
								></bee-date-input>
							</v-col>
				
							<!-- execution date -->
							<v-col sm="6" cols="12">
								<bee-date-input
									v-model="dialogData.executionDate"
									:label="$T('inputs.execution-date')"
									hide-details
									dense
									prepend-icon="$calendar"
									:menu-picker-months-over-max="3"
									user-menu-picker
									:rules="[rules.required]"
								></bee-date-input>
							</v-col>
							
							<!-- notes -->
							<v-col cols="12">
								<v-textarea
									v-model="dialogData.notes"
									:label="$T('inputs.notes')"
									rows="2"
									auto-grow
									hide-details
									outlined
									dense
								></v-textarea>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							type="submit"
							color="secondary"
							:loading="submitLoading"
						>
							{{ $T('save') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			module="penaltiesRewards/delete"
			:parameters="{ id: selectedItem.id }"
			@on-delete="needToResetTable = true; filter();"
		>
			{{
				+tab === penaltyRewardType.penalty ? 
				$T('PenaltiesAndRewards.delete-message-penalty') :  
				$T('PenaltiesAndRewards.delete-message-bonus')
			}}
		</v-delete-dialog>

		<!-- tabs -->
		<v-tabs
			v-model="tab"
			background-color="light-primary"
			active-class="mid-primary"
			color="transparent"
			@change="needToResetTable = true; filter()"
		>
			<v-tab
				class="white--text"
				:href="`#${penaltyRewardType.penalty}`">
				<v-icon
					color="white"
					class="me-1">
					mdi-alert-octagon-outline
				</v-icon>
				{{ $T('tabs.penalties') }}
			</v-tab>
			<v-tab
				class="white--text"
				:href="`#${penaltyRewardType.rewards}`">
				<v-icon
					color="white"
					class="me-1">
					mdi-gift-outline
				</v-icon>
				{{ $T('tabs.rewards') }}
			</v-tab>
		</v-tabs>

		<!-- filters -->
		<div class="pa-2">
			<v-form @submit.prevent="needToResetTable = true; filter();">
				<v-row align="center" dense class="mb-1">
					<v-col v-if="false" cols="6" sm="4" md="2">
						<v-autocomplete
							v-model="filterData.employee"
							:items="sections"
							item-text="name"
							item-value="id"
							:label="$T('inputs.search-employee')"
							dense
							outlined
							hide-details
						></v-autocomplete>
					</v-col>
				
					<!-- sections -->
					<v-col cols="6" sm="4" md="2">
						<v-autocomplete
							v-model="filterData.sectionId"
							:items="sections"
							item-text="name"
							item-value="id"
							clearable
							dense
							outlined
							:label="$T('inputs.search-section')"
							hide-details></v-autocomplete>
					</v-col>

					<!-- type penOrRew -->
					<v-col cols="6" sm="4" md="2">
						<v-autocomplete
							v-model="filterData.punishmentTypeId"
							:items="+tab === penaltyRewardType.penalty ? penalties : rewards"
							item-text="name"
							item-value="id"
							clearable
							dense
							outlined
							:label="+tab === penaltyRewardType.penalty ? 'نوع العقوبة' : 'نوع المكافأة' "
							hide-details></v-autocomplete>
					</v-col>
				
					<!-- dates -->
					<v-col cols="9" sm="7" md="3" class="d-flex">
						<bee-date-input
							v-model="filterData.fromDate"
							:label="$T('inputs.from-date')"
							class="styled"
							dense
							hide-details
							changeable-with-arrows
							changeable-on-scroll
							prepend-icon="$calendar"
							user-menu-picker
						></bee-date-input>
				
						<bee-date-input
							v-model="filterData.toDate"
							:label="$T('inputs.to-date')"
							class="styled"
							dense
							hide-details
							changeable-with-arrows
							changeable-on-scroll
							prepend-icon="$calendar"
							user-menu-picker
						></bee-date-input>
					</v-col>
				
					<v-col cols="auto">
						<v-btn
							type="submit"
							:disabled="Boolean(filterData.employee && filterData.sectionId && filterData.fromDate && filterData.toDate && filterData.punishmentTypeId)"
							class="me-1"
							outlined
							small
							color="primary"
						>
							<v-icon small left>mdi-magnify</v-icon>
							{{ $T('default-buttons.search') }}
						</v-btn>
						<v-btn
							v-if="filterData.employee || filterData.sectionId || filterData.fromDate || filterData.toDate || filterData.punishmentTypeId"
							style="min-width: 30px"
							class="px-0"
							outlined
							small
							color="alert"
							@click="needToResetTable = true; clearFilters(); filter()"
						>
							<v-icon small>$clear</v-icon>
						</v-btn>
					</v-col>
				
					<v-spacer></v-spacer>
					<v-divider vertical class="mx-1"/>
					<v-col cols="auto">
						<v-btn color="primary" small @click="addEditDialog = true">
							<v-icon size="20" left>
								$add
							</v-icon>
							{{ +tab === penaltyRewardType.penalty ? $T('add-penalty') : $T('add-reward') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-form>

			<!-- table -->
			<bee-handy-table
				ref="table"
				:headers="headers"
				:items="[]"
				:items-per-page="25"
				:data-table-props="{ disableSort: true }"
				:loading="loading"
				dense
				zebra
				fixed-header
				pagination-on-scroll
				pagination-on-scroll-server-items
				:server-items-length="totalItems"
				:options-scroll.sync="scrollOptions"
				:height="$vuetify.breakpoint.height - 130"
				:shown-rows-text="$T('shown-rows-text')"
				@pagination-on-scroll:reach-last-row="filter"
			>
				<template v-slot:item.actions="{ item }">
					<div class="d-flex">
						<v-btn icon color="edit-color" @click="selectedItem = item; addEditDialog = true;">
							<v-icon>$edit</v-icon>
						</v-btn>
						<v-btn icon color="alert" @click="selectedItem = item; deleteDialog = true;">
							<v-icon>$delete</v-icon>
						</v-btn>
					</div>
				</template>
			</bee-handy-table>
		</div>
	</div>
</template>

<script>
import { DATE_FORMAT } from '@/utils/moment';
import { penaltyRewardType, penaltyRewardValueType, penaltyRewardValueTypes } from '@/utils/enum';
import { mapGetters, mapState } from 'vuex';
import rules from '@/utils/validation rules';
import moment from 'moment'

export default {
	name: 'PenaltiesAndRewards',

	data() {
		return {
			tab: null,
			addEditDialog: false,
			dialogData: {
				employeeId: null,
				typeId: null,
				effectType: null,
				typeValue: null,
				suggestedByEmployeeId: null,
				date: null,
				executionDate: null,
				notes: null,
			},
			selectedItem: {},
			deleteDialog: false,

			filterData: {
				employee: null,
				sectionId: null,
				fromDate: moment(moment().startOf('month')).format('YYYY-MM-DD'),
				toDate: moment(moment().endOf('month')).format('YYYY-MM-DD'),
				punishmentTypeId: null
			},

			// helpers
			loading: false,
			submitLoading: false,
			needToResetTable: false,
			totalItems: 0,
			scrollOptions: {},

			penaltyRewardType,
			penaltyRewardValueType,
			penaltyTypes: penaltyRewardValueTypes,
			rewordsTypes: penaltyRewardValueTypes.slice(0, 2),
			DATE_FORMAT,
			rules
		};
	},

	watch: {
		addEditDialog(val) {
			if (val) {
				if (this.selectedItem.id) {
					this.dialogData.employeeId = this.selectedItem.employeeId;
					this.dialogData.suggestedByEmployeeId = this.selectedItem.suggestedByEmployeeId;
					this.dialogData.date = this.selectedItem.date;
					this.dialogData.executionDate = this.selectedItem.dateOfStarting;
					this.dialogData.typeId = this.selectedItem.typeId;
					this.dialogData.effectType = this.selectedItem.valueType;
					this.dialogData.typeValue = this.selectedItem.value;
					this.dialogData.notes = this.selectedItem.notes;
				}
				this.$nextTick(() => this.$refs.employeeInput.focus());
			} else {
				this.$refs.form.reset();
				this.selectedItem = {};
			}
		},
		deleteDialog(val) {
			if (!val) { this.selectedItem = {}; }
		}
	},

	computed: {
		...mapState({
			employees: state => state.employee.employees,
			sections: state => state.section.sections,
			penalties: state => state.penaltiesRewards.penalties,
			rewards: state => state.penaltiesRewards.rewards,
		}),

		...mapGetters({
			getPenaltyById: 'penaltiesRewards/getPenaltyById',
			getRewardById: 'penaltiesRewards/getRewardById',
		}),

		headers() {
			const headers = [
				{
					text: this.$T('headers.code'),
					name: 'code',
					value: 'employee.code',
					width: 40
				},
				{
					text: this.$T('headers.employee-name'),
					name: 'name',
					value: 'employee.arabicFullName',
				},
				{
					text: this.$T('headers.section-name'),
					name: 'section',
					value: 'employee.sectionName',
				},
				{
					text: this.$T('headers.type'),
					name: 'type',
					value: 'punishmentRewardTypes.name',
				},
				{
					text: this.$T('headers.date'),
					name: 'date',
					value: 'date',
					type: 'date',
					dateFormat: DATE_FORMAT,
				},
				{
					text: this.$T('headers.execution-date'),
					name: 'executionDate',
					value: 'dateOfStarting',
					type: 'date',
					dateFormat: DATE_FORMAT,
				},
				{
					text: this.$T('headers.details'),
					name: 'details',
					value: 'notes',
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 40,
				},
			];
			return headers;
		},

		typeValueLabel() {
			switch(this.dialogData.effectType){
			case penaltyRewardValueType.rate: return this.$T('inputs.percent');
			case penaltyRewardValueType.days: return this.$T('inputs.days-count');
			default: return this.$T('inputs.value');
			}
		}
	},

	methods: {
		moment,
		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.selectedItem.id ? 'create' : 'update';
				this.$store.dispatch(`penaltiesRewards/${actionName}`, {
					id: this.selectedItem.id,
					employeeId: this.dialogData.employeeId,
					suggestedByEmployeeId: this.dialogData.suggestedByEmployeeId,
					typeId: this.dialogData.typeId,
					date: this.dialogData.date,
					dateOfStarting: this.dialogData.executionDate,
					notes: this.dialogData.notes,
					valueType: this.dialogData.effectType,
					value: this.dialogData.typeValue
				}).then(() => {
					this.needToResetTable = true;
					this.filter();
					this.$eventBus.$emit(
						'show-snackbar',
						!this.selectedItem.id ? this.$T('messages.add-done') : this.$T('messages.edit-done'),
						{ done: true }
					);
					this.addEditDialog = false;
				}).catch((err) => {
					err.default && err.default(
						!this.selectedItem.id ? this.$T('messages.add-fail') : this.$T('messages.edit-fail'),
						false
					);
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		filter(newScrollOptions) {
			this.loading = true;
			let page = 1;
			// const { itemsPerPage } = this.scrollOptions;

			if (newScrollOptions) {
				page = newScrollOptions.page
			}
			if (this.needToResetTable) {
				this.$refs.table?.resetShownRows();
				this.needToResetTable = false;
			}

			this.$store.dispatch('penaltiesRewards/fetchAll', {
				type: this.tab,
				dateFrom: this.filterData.fromDate,
				dateTo: this.filterData.toDate,
				sectionId: this.filterData.sectionId,
				page,
				perPage: 20,
				employeeIds: [],
				punishmentTypeId: this.filterData.punishmentTypeId
			}).then(({ data, total }) => {
				this.totalItems = total;
				this.$refs.table?.addScrollItems(data);
			}).finally(() => {
				this.loading = false;
			})
		},

		clearFilters() {
			this.filterData = {
				employee: null,
				sectionId: null,
				fromDate: null,
				toDate: null,
			};
		},

		getTypeDueCurrentTab(id) {
			return this[+this.tab === penaltyRewardType.penalty ? 'getPenaltyById' : 'getRewardById'](id)
		}
	},

	created() {
		if (this.penalties.length === 0 || this.rewards.length === 0) {
			this.$store.dispatch('penaltiesRewards/fetchAllTypes')
		}
	}
};
</script>

<style></style>
